import * as React from "react";
import { Activity } from "lucide-react";

import { useGetGroupingJobQuery } from "@/api/contract-families";
import { Progress } from "@/components/ui/progress";
import type { GroupingJobResponse } from "@/types/contract-family";

const terminalGroupingStages = new Set(["complete", "failed"]);

/** Polling cadence for a grouping job: every 2 s while it runs, off once it reached a terminal stage. */
const groupingJobPollingInterval = (stage: string | undefined): number =>
  stage !== undefined && terminalGroupingStages.has(stage) ? 0 : 2000;

interface Props {
  txId: string;
  onTerminalStatus?: (job: GroupingJobResponse) => void;
}

export const GroupingJobProgress: React.FC<Props> = ({ txId, onTerminalStatus }) => {
  // `txId` stays in the URL after the job finishes, so the interval must drop to 0 on a terminal stage
  // or the page would keep polling a finished job for as long as it is open.
  const [stage, setStage] = React.useState<string | undefined>(undefined);
  const { data, isError, isLoading } = useGetGroupingJobQuery(txId, {
    pollingInterval: groupingJobPollingInterval(stage),
    skipPollingIfUnfocused: true,
  });
  React.useEffect(() => {
    setStage(data?.stage);
  }, [data?.stage]);
  const lastNotifiedTerminalStatus = React.useRef<string | null>(null);
  const progress = Math.max(0, Math.min(100, Math.round((data?.progress ?? 0) * 100)));

  React.useEffect(() => {
    if (!data || !terminalGroupingStages.has(data.stage)) {
      return;
    }

    const terminalStatusKey = `${data.tx_id}:${data.stage}`;
    if (lastNotifiedTerminalStatus.current === terminalStatusKey) {
      return;
    }

    lastNotifiedTerminalStatus.current = terminalStatusKey;
    onTerminalStatus?.(data);
  }, [data, onTerminalStatus]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 text-xs text-slate-600">
        <Activity className="h-3.5 w-3.5" />
        Loading grouping job...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
        Unable to load grouping job status.
      </div>
    );
  }

  return (
    <div className="grid gap-2 border-b border-slate-200 bg-white px-4 py-2 text-xs text-slate-700 md:grid-cols-[180px_1fr_60px] md:items-center">
      <div className="flex items-center gap-2 font-medium text-slate-900">
        <Activity className="h-3.5 w-3.5 text-blue-700" />
        <span>{data.stage}</span>
      </div>
      <Progress value={progress} className="h-2 bg-slate-100" />
      <div className="text-right font-mono text-slate-900">{progress}%</div>
    </div>
  );
};
